/**
 * Help / documentation topic registry.
 *
 * Pure, framework-free description of every user-facing help topic: which
 * application routes it documents, how it is grouped in the Documentation
 * Center, which icon and illustration represent it, and the fallback logic
 * that maps the current route to a topic.
 *
 * All human-readable strings live in the `help` i18next namespace
 * (the per-language `help.json` files) keyed by the topic `id`; this module
 * holds only structural metadata so it can be unit-tested without React or i18n.
 */

/** Sidebar-style grouping used by the Documentation Center left rail. */
export type HelpGroupId = 'gettingStarted' | 'dailyOps' | 'metals' | 'accounting' | 'admin';

/** Order in which groups are displayed in the Documentation Center. */
export const HELP_GROUP_ORDER: HelpGroupId[] = [
    'gettingStarted',
    'dailyOps',
    'metals',
    'accounting',
    'admin',
];

export interface HelpTopic {
    /** Stable id; also the key under `topics.<id>` in the help namespace. */
    id: string;
    /** Application routes this topic documents (used for route -> topic matching). */
    routes: string[];
    /** Icon key resolved to a concrete icon in the view layer. */
    iconKey: string;
    /** Group the topic belongs to in the Documentation Center. */
    group: HelpGroupId;
    /** Optional illustration key resolved by the illustration registry. */
    illustration?: string;
}

/** Id of the business-workflow overview topic, used as the default/fallback. */
export const OVERVIEW_TOPIC_ID = 'overview';

/**
 * Every documented topic. `overview` is the workflow landing topic and the
 * fallback when the current route has no more specific topic.
 */
export const helpTopics: HelpTopic[] = [
    {
        id: OVERVIEW_TOPIC_ID,
        routes: ['/'],
        iconKey: 'overview',
        group: 'gettingStarted',
        illustration: 'contractLifecycle',
    },
    { id: 'dashboard', routes: ['/dashboard'], iconKey: 'dashboard', group: 'gettingStarted' },
    {
        id: 'contracts',
        routes: ['/contracts', '/contracte'],
        iconKey: 'contracts',
        group: 'dailyOps',
        illustration: 'contractLifecycle',
    },
    {
        id: 'addContract',
        routes: ['/contracts/add'],
        iconKey: 'contracts',
        group: 'dailyOps',
        illustration: 'stepFlow',
    },
    {
        id: 'payment',
        routes: ['/contracts/payment'],
        iconKey: 'pred_casa',
        group: 'dailyOps',
        illustration: 'redemptionInterest',
    },
    { id: 'clients', routes: ['/clients'], iconKey: 'clients', group: 'dailyOps' },
    {
        id: 'goodsSale',
        routes: ['/goods-sale'],
        iconKey: 'goods-sale',
        group: 'dailyOps',
    },
    {
        id: 'inventoryShop',
        routes: ['/inventory'],
        iconKey: 'inventory-shop',
        group: 'dailyOps',
    },
    {
        id: 'cashDrawer',
        routes: ['/pred_casa'],
        iconKey: 'pred_casa',
        group: 'dailyOps',
        illustration: 'cashHandover',
    },
    {
        id: 'sessionVerification',
        routes: ['/frmverifylastsession'],
        iconKey: 'frmverifylastsession',
        group: 'dailyOps',
        illustration: 'shiftVerification',
    },
    { id: 'dispozitii', routes: ['/dispozitii'], iconKey: 'dispozitii', group: 'dailyOps' },
    { id: 'changeUser', routes: ['/schutil'], iconKey: 'schutil', group: 'dailyOps' },
    {
        id: 'metals',
        routes: ['/metals'],
        iconKey: 'metals',
        group: 'metals',
        illustration: 'metalFlow',
    },
    {
        id: 'inventoryMovement',
        routes: ['/inventory-movement'],
        iconKey: 'inventory-movement',
        group: 'metals',
        illustration: 'contractLifecycle',
    },
    { id: 'controlRoom', routes: ['/control-room'], iconKey: 'control-room', group: 'accounting' },
    { id: 'statistics', routes: ['/statistics'], iconKey: 'statistics', group: 'accounting' },
    { id: 'reports', routes: ['/reports'], iconKey: 'reports', group: 'accounting' },
    { id: 'rapoarte', routes: ['/rapoarte'], iconKey: 'rapoarte', group: 'accounting' },
    { id: 'contabilitate', routes: ['/contabilitate'], iconKey: 'contabilitate', group: 'accounting' },
    { id: 'storno', routes: ['/frmstorno'], iconKey: 'frmstorno', group: 'accounting' },
    { id: 'listings', routes: ['/frmlistari'], iconKey: 'frmlistari', group: 'accounting' },
    { id: 'registru', routes: ['/regiszter'], iconKey: 'regiszter', group: 'accounting' },
    { id: 'salary', routes: ['/salary'], iconKey: 'salary', group: 'admin' },
    { id: 'advanced', routes: ['/advanced'], iconKey: 'advanced', group: 'admin' },
    { id: 'configuration', routes: ['/configuration'], iconKey: 'configuration', group: 'admin' },
    { id: 'changelog', routes: ['/changelog'], iconKey: 'changelog', group: 'admin' },
];

const overviewTopic = helpTopics.find(topic => topic.id === OVERVIEW_TOPIC_ID) as HelpTopic;

/** Normalise a pathname: strip query/hash, trailing slash, and lowercase. */
function normalisePath(pathname: string): string {
    const withoutQuery = pathname.split(/[?#]/)[0].toLowerCase();
    if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
        return withoutQuery.slice(0, -1);
    }
    return withoutQuery;
}

/** True when `route` is an exact match or a path-segment prefix of `path`. */
function routeMatches(route: string, path: string): boolean {
    return path === route || path.startsWith(`${route}/`);
}

/**
 * Resolve the help topic that documents the given route. Uses longest-prefix
 * matching so `/contracts/add` maps to the New Contract topic rather than the
 * Contracts list. Falls back to the workflow overview when nothing matches.
 */
export function resolveHelpTopic(pathname: string): HelpTopic {
    const path = normalisePath(pathname);
    let best: HelpTopic = overviewTopic;
    let bestLength = -1;

    for (const topic of helpTopics) {
        for (const route of topic.routes) {
            if (routeMatches(route, path) && route.length > bestLength) {
                best = topic;
                bestLength = route.length;
            }
        }
    }

    return best;
}

/** Look up a topic by id. */
export function getHelpTopic(id: string): HelpTopic | undefined {
    return helpTopics.find(topic => topic.id === id);
}

/** Topics belonging to a group, in registry order. */
export function getTopicsByGroup(group: HelpGroupId): HelpTopic[] {
    return helpTopics.filter(topic => topic.group === group);
}
